/**
 * Capped ring-buffer event store for the Dash devtools panel.
 *
 * Mirrors `vercel-labs/json-render`'s `EventStore`. Holds the most
 * recent N events (default 200) — older events fall off the back when
 * the buffer fills. Subscribers are notified after each `record` so
 * the React panel re-renders.
 */

import type { ActionDispatchInfo, ActionSettleInfo } from "../dashboard/action-observer"

/**
 * Token usage report from a single AI generation.
 *
 * Mirrors the shape both Anthropic and OpenAI return on their stream
 * responses; cached/cacheWrite are optional because not every model
 * exposes them. All counts are integers.
 */
export interface TokenUsage {
  promptTokens: number
  completionTokens: number
  totalTokens: number
  cachedTokens?: number
  cacheWriteTokens?: number
  /** Optional model id so the HUD can show "gpt-5-mini · 12k tokens". */
  model?: string
}

/**
 * One devtools event. Discriminated union keyed by `kind`.
 */
export type DevtoolsEvent =
  | { kind: "action-dispatched"; at: number; info: ActionDispatchInfo }
  | { kind: "action-settled"; at: number; info: ActionSettleInfo }
  | { kind: "state-changed"; at: number; snapshot: Record<string, unknown> }
  | { kind: "spec-changed"; at: number; specJson: string }
  | {
      kind: "stream-event"
      at: number
      phase: "start" | "chunk" | "end" | "error"
      detail?: string
    }
  | { kind: "stream-usage"; at: number; usage: TokenUsage }

export interface EventStoreOptions {
  /** Maximum number of events retained. Older events are dropped. */
  maxEvents?: number
}

export interface EventStore {
  /** Append an event; oldest is evicted if the buffer is full. */
  record(event: DevtoolsEvent): void
  /** Snapshot the current buffer (newest first). */
  list(): DevtoolsEvent[]
  /** Subscribe to buffer changes. Returns an unsubscribe function. */
  subscribe(listener: () => void): () => void
  /** Drop everything. */
  clear(): void
  /** How many events are currently buffered. */
  size(): number
}

export function createEventStore(options: EventStoreOptions = {}): EventStore {
  const maxEvents = Math.max(1, options.maxEvents ?? 200)
  // Newest events are at the END of the array; we slice() before exposing
  // to keep the snapshot independent of subsequent record() calls.
  const buffer: DevtoolsEvent[] = []
  const listeners = new Set<() => void>()
  // Cached newest-first snapshot. ``list()`` is called by React's
  // useSyncExternalStore on every render — returning a fresh `[...].reverse()`
  // each time produces a new array reference, which React reads as
  // "snapshot changed", schedules another render, and infinite-loops with
  // "Maximum update depth exceeded" (React error #185). Caching here keeps
  // referential identity stable between record() calls. Invalidated to
  // null on every record / clear; rebuilt lazily on the next list() call.
  let snapshot: DevtoolsEvent[] | null = []

  function notify(): void {
    for (const l of listeners) {
      try {
        l()
      } catch {
        // Listeners are panel-internal — never let a UI throw take down recording.
      }
    }
  }

  return {
    record(event) {
      buffer.push(event)
      if (buffer.length > maxEvents) {
        buffer.splice(0, buffer.length - maxEvents)
      }
      snapshot = null
      notify()
    },
    list() {
      // Newest first for display; the buffer is appended chronologically.
      // Cached so React's useSyncExternalStore sees a stable reference
      // across renders that didn't follow a record() / clear().
      if (snapshot === null) {
        snapshot = [...buffer].reverse()
      }
      return snapshot
    },
    subscribe(listener) {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    clear() {
      if (buffer.length === 0) return
      buffer.length = 0
      snapshot = null
      notify()
    },
    size() {
      return buffer.length
    },
  }
}
